/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import DataManager from '@americanexpress/data-manager';
import SharedScripts, { Adobe } from '@americanexpress/shared-scripts';
import { DDLRegister } from '@americanexpress/react-ddl';
import { AmexDDLRegister, getAmexDataManagerConfig } from '@americanexpress/data-manager-utils';
import UpdateViewDDL from './UpdateViewDDL';
import { getDeviceType, getValueFromCookie } from '../utils';
import AnalyticsErrorBoundary from './AnalyticsErrorBoundary';
import { version } from '../../package.json';

export const generateAnalyticsEvent = ({ type, elementProp, reactlytics = {} }) => {
  const { detail, element } = reactlytics;
  return {
    element: element || elementProp,
    type: reactlytics.type || type,
    detail,
    meta: {
      ...reactlytics.meta,
    },
  };
};

export const getTargetNodeName = (syntheticEvent = {}) => {
  const event = syntheticEvent.nativeEvent || {};
  const element = event.target || {};
  return element.nodeName;
};

export const customHandlerCreators = () => ({
  onClick: (track, handler, props) => (syntheticEvent) => {
    const { type = undefined, reactlytics = {} } = props;
    if (reactlytics.disable) { return; }
    const defaultElement = getTargetNodeName(syntheticEvent);
    const elementProp = type || defaultElement;
    const event = generateAnalyticsEvent({ elementProp, reactlytics, type: 'click' });
    track({ event });
  },
  onSubmit: (track, handler, props) => (syntheticEvent) => {
    const { id = undefined, reactlytics = {} } = props;
    if (reactlytics.disable) { return; }
    const defaultElement = getTargetNodeName(syntheticEvent);
    const elementProp = id || defaultElement;
    const event = generateAnalyticsEvent({ elementProp, reactlytics, type: 'submit' });
    track({ event });
  },
  onDismiss: (track, handler, props) => (syntheticEvent) => {
    const { label = undefined, type = undefined, reactlytics = {} } = props;
    if (reactlytics.disable) { return; }
    const defaultElement = getTargetNodeName(syntheticEvent);
    const elementProp = label || type || defaultElement;
    const event = generateAnalyticsEvent({ elementProp, reactlytics, type: 'dismiss' });
    track({ event });
  },
  onChange: (track, handler, props) => (syntheticEvent) => {
    const { id = undefined, reactlytics = {} } = props;
    if (reactlytics.disable) { return; }
    const defaultElement = getTargetNodeName(syntheticEvent);
    const elementProp = id || defaultElement;
    const event = generateAnalyticsEvent({ elementProp, reactlytics, type: 'change' });
    track({ event });
  },
  onFocus: (track, handler, props) => (syntheticEvent) => {
    const { id = undefined, reactlytics = {} } = props;
    if (reactlytics.disable) { return; }
    const defaultElement = getTargetNodeName(syntheticEvent);
    const elementProp = id || defaultElement;
    const event = generateAnalyticsEvent({ elementProp, reactlytics, type: 'focus' });
    track({ event });
  },
  onBlur: (track, handler, props) => (syntheticEvent) => {
    const { id = undefined, reactlytics = {} } = props;
    if (reactlytics.disable) { return; }
    const defaultElement = getTargetNodeName(syntheticEvent);
    const elementProp = id || defaultElement;
    const event = generateAnalyticsEvent({ elementProp, reactlytics, type: 'blur' });
    track({ event });
  },
  default: (track, handler, props) => (syntheticEvent) => {
    const { reactlytics = {} } = props;
    if (reactlytics.disable) { return; }
    const defaultElement = getTargetNodeName(syntheticEvent);
    const event = generateAnalyticsEvent({ elementProp: defaultElement, reactlytics, type: reactlytics.type || 'event' });
    track({ event });
  },
});

function DataLayer({
  locale,
  children,
}) {
  const [isUserAuthenticated, setIsUserAuthenticated] = useState(false);

  /* istanbul ignore next */
  useEffect(() => {
    const interval = setInterval(() => {
      setIsUserAuthenticated(window?.AmexSession?.isAuthenticated());

      if (window?.AmexSession?.isAuthenticated()) {
        clearInterval(interval);
      }
    }, 500);

    return () => clearInterval(interval);
  }, []);

  const obj = global.BROWSER ? {
    initialDDL: {
      historyLimit: 700,
      ddl: {
        version: '1.7.0',
        charset: 'UTF-8',
        components: {
          root: {
            componentId: 'axp-card-member-apply-root',
            version,
            timestamp: new Date().toISOString(),
            impressed: true,
          },
        },
        diff: ['events'],
        events: [],
        campaigns: {},
        timestamp: new Date().toISOString(),
        device: {
          userAgent: window.navigator.userAgent,
          width: {
            px: window.screen.width,
          },
          height: {
            px: window.screen.height,
          },
          channel: getDeviceType(navigator.userAgent),
        },
        view: {
          country: locale.split('-')[1],
          language: locale.split('-')[0],
          locale,
          location: window.location.href,
          timestamp: new Date().toISOString(),
          origin: window.location.hostname,
          title: 'American Express - American Express',
          viewId: window.location.pathname,
        },
      },
    },
  } : {};
  const publicGuid = getValueFromCookie('blueboxpublic');

  return (
    <DataManager
      {...getAmexDataManagerConfig({
        ...obj,
        reactlyticsProps: {
          handlerCreators: customHandlerCreators(),
        },
      })}
    >
      {publicGuid && <DDLRegister name="user.publicGuid" value={publicGuid} />}
      <DDLRegister name="user.authenticated" value={isUserAuthenticated} />
      <SharedScripts
        name="one-stream-data-handler"
        windowCheck="oneStreamData"
        version="^0.1.2"
      />
      <AnalyticsErrorBoundary>
        <AmexDDLRegister />
        <Adobe async={true} version="^1.0.0" />
      </AnalyticsErrorBoundary>
      <UpdateViewDDL locale={locale} />
      {children}
    </DataManager>
  );
}

DataLayer.propTypes = {
  channel: PropTypes.string,
  locale: PropTypes.string.isRequired,
  pathname: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export default DataLayer;
